// Store
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  OnChanges,
  SecurityContext,
  SimpleChanges
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { DeviceDetectorService } from 'ngx-device-detector';
import { take } from 'rxjs/operators';

import { PolicyDocumentsActions } from '@amfam/policy/data-access';
import { PolicyDetails, PolicyDocument } from '@amfam/policy/models';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { OverviewAnalytics } from '@amfam/shared/models';
import { DocumentPipeService } from '@amfam/shared/ui/pipes';
import { CopyService, openWindowBlob, WindowRef } from '@amfam/shared/utility/shared-services';
import { DsModalService, LoadingSpinnerService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';

@Component({
  selector: 'policy-documents',
  templateUrl: './policy-documents.component.html',
  styleUrls: ['./policy-documents.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PolicyDocumentsComponent implements OnChanges {
  @Input() documents: PolicyDocument[];
  @Input() documentType: string;
  @Input() startDate?: Date;
  @Input() endDate?: Date;
  @Input() cancelDate?: Date;

  @Input() hasCancelDate: boolean;
  hasEffectiveDatesChangedModalId = 'hasEffectiveDatesChangedModalId';
  POIDocument: any;

  constructor(
    private store: Store<fromRoot.RootState>,
    private spinner: LoadingSpinnerService,
    private win: WindowRef,
    private documentPipeService: DocumentPipeService,
    private sanitizer: DomSanitizer,
    private copyService: CopyService,
    private analyticsFacade: AnalyticsFacade,
    private deviceService: DeviceDetectorService,
    private breakPointObservable: BreakpointObserver,
    private modalService: DsModalService
  ) {}
  @HostListener('window:resize')
  resize() {
    this.loadDocuments();
  }
  ngOnChanges(changes: SimpleChanges): void {
    this.loadDocuments();
  }

  showAll = false;
  loadedDocuments = [];

  checkCancelDateAndOpenDocument(document) {
    // Open modal if has cancel date else open document as usual
    this.POIDocument = document;
    if (this.hasCancelDate) {
      // Open modal
      this.modalService.open(this.hasEffectiveDatesChangedModalId);
    } else {
      this.viewDocument(document);
    }
  }

  viewPOIDocument() {
    this.viewDocument(this.POIDocument);
    this.modalService.close(this.hasEffectiveDatesChangedModalId);
  }

  toggleShowAll() {
    this.showAll = !this.showAll;
    this.loadDocuments();
  }

  loadDocuments() {
    const isSmallScreen = this.breakPointObservable.isMatched(
      '(max-width: 480px) and (orientation: portrait)'
    );
    this.documents = this.mapDocuments(this.sortDocuments(this.documents));

    const documentsLength = isSmallScreen ? 3 : 4;
    if (this.showAll) {
      this.loadedDocuments = this.documents || [];
    } else {
      this.loadedDocuments = (this.documents || []).slice(0, documentsLength);
    }
  }
  viewDocumentOnAndroid(document) {
    this.spinner.start();
    let windowRef;
    if (!window.navigator) {
      windowRef = this.win.nativeWindow.open();
    }
    if (this.isPOICard(document))
      this.analyticsFacade.trackButtonClick(OverviewAnalytics.documentsGetPOIButton);
    this.documentPipeService
      .retrieveDocument(document.linksToSelf[0].href)
      .pipe(take(1))
      .subscribe(
        res => {
          if (!res && this.isPOICard(document))
            this.analyticsFacade.trackPage(OverviewAnalytics.documentsGetPOIError);
          if (!res) {
            if (windowRef) {
              windowRef.close();
            }
          }
          this.spinner.stop();
          if (res) {
            const url = this.sanitizer.sanitize(SecurityContext.URL, res);
            openWindowBlob(this.win, url, res, windowRef, 'document.pdf');
          }
        },
        err => {
          if (this.isPOICard(document))
            this.analyticsFacade.trackPage(OverviewAnalytics.documentsGetPOIError);
          if (windowRef) {
            windowRef.close();
          }
        }
      );
  }

  viewDocument(document) {
    this.spinner.start();
    if (this.deviceService.device.toLowerCase() === 'android') this.viewDocumentOnAndroid(document);
    else {
      let windowRef;
      windowRef = this.win.nativeWindow.open();
      if (this.isPOICard(document))
        this.analyticsFacade.trackButtonClick(OverviewAnalytics.documentsGetPOIButton);
      this.documentPipeService
        .retrieveDocument(document.linksToSelf[0].href)
        .pipe(take(1))
        .subscribe(
          res => {
            if (res && this.isPOICard(document)) {
              this.analyticsFacade.trackButtonClick(PolicyDetails.getPOIPDF);
            }
            if (res && this.isDECPage(document)) {
              this.analyticsFacade.trackButtonClick(PolicyDetails.getDECPagePDF);
            }
            if (!res && this.isPOICard(document)) {
              this.analyticsFacade.trackPage(OverviewAnalytics.documentsGetPOIError);
            }
            if (!res) {
              this.analyticsFacade.trackEvent({
                event: 'policy_document_download_failure',
                method: document.title,
                error_code: 'POLICYDOCFAILURE'
              });
              if (windowRef) {
                windowRef.close();
              }
            }
            this.spinner.stop();
            if (res) {
              this.analyticsFacade.trackEvent({
                event: 'policy_document_download_success',
                method: document.title
              });
              const url = this.sanitizer.sanitize(SecurityContext.URL, res);
              windowRef.location.href = url;
            }
          },
          err => {
            if (this.isPOICard(document))
              this.analyticsFacade.trackPage(OverviewAnalytics.documentsGetPOIError);
            if (windowRef) {
              windowRef.close();
            }
          }
        );
    }
    this.store.dispatch(PolicyDocumentsActions.ViewAction(document.id));
  }

  private isPOICard(document) {
    return (
      document &&
      document.metadata['Content Description'] ===
        this.copyService.getCopy('shared.poiCardDocTitle')
    );
  }
  private isDECPage(document) {
    return document && document.metadata['Content Description'] === 'POLICY PACKAGE';
  }

  private sortDocuments(documents: PolicyDocument[]): PolicyDocument[] {
    const sortedPolicyDocuments = documents.sort((a, b) => {
      const doc0 = a.metadata['Date On Document'];
      const doc1 = b.metadata['Date On Document'];
      const doc0EffectiveDate = a.metadata['Effective Date'];
      const doc1EffectiveDate = b.metadata['Effective Date'];

      if (
        doc0 === undefined ||
        doc1 === undefined ||
        doc0EffectiveDate === undefined ||
        doc1EffectiveDate === undefined
      ) {
        return 0; // Handle invalid data, returning 0 as default
      }

      const dateOnDoc0 = this.convertDateShort(doc0);
      const dateOnDoc1 = this.convertDateShort(doc1);
      const dateOnEffective0 = this.convertDateShort(doc0EffectiveDate);
      const dateOnEffective1 = this.convertDateShort(doc1EffectiveDate);

      if (
        dateOnDoc0 === undefined ||
        dateOnDoc1 === undefined ||
        dateOnEffective0 === undefined ||
        dateOnEffective1 === undefined
      ) {
        return 0; // Handle invalid date conversion, returning 0 as default
      }

      const today = new Date();

      if (dateOnDoc0.getTime() !== dateOnDoc1.getTime()) {
        return dateOnDoc0.getTime() > dateOnDoc1.getTime() ? -1 : 1;
      } else {
        // if effective date is greater than today date
        const isFirstDocFutureEffectiveDate = dateOnEffective0.getTime() > today.getTime();
        const isSecondDocFutureEffectiveDate = dateOnEffective1.getTime() > today.getTime();

        // if one is future and other is past/current
        if (isFirstDocFutureEffectiveDate !== isSecondDocFutureEffectiveDate) {
          return isSecondDocFutureEffectiveDate ? -1 : 1;
        } else {
          // if both are future or both are past/current
          return dateOnEffective0.getTime() > dateOnEffective1.getTime() ? -1 : 1;
        }
      }
    });
    let poiIndex = 0;
    return sortedPolicyDocuments.map(pd => {
      if (pd.isPoi) {
        pd.poiIndex = poiIndex;
        poiIndex += 1;
      }
      return pd;
    });
  }

  private convertDateShort(dateString: string): Date | undefined {
    // Implement your date conversion logic here
    return new Date(dateString);
  }

  private mapDocuments(documents: PolicyDocument[]) {
    return documents.map(document => ({
      ...document,
      title:
        this.documentType === 'life'
          ? document.metadata['Content Description']
          : document.metadata['Document Title'],
      subTitle:
        document.sourceSystemName === 'Rome'
          ? null
          : this.documentType === 'life'
            ? document.metadata['Category Type']
            : document.documentSubHeading
    }));
  }
}
